// Raw data log (device page): the tracker's records in a period, newest first, with every value
// it sent, and a CSV of all records in the period (up to 7 days) for deeper digging.
import { useEffect, useState } from 'react';
import { RecordsPage, trackers } from '../api';
import { dayLabel, duration, time } from '../format';
import { DataGrid } from './DataGrid';

const PERIODS = [['1h', 'Last hour', 1], ['6h', '6 h', 6], ['24h', '24 h', 24], ['7d', '7 days', 168]] as const;
type Period = (typeof PERIODS)[number][0];

const ioText = (io: Record<string, unknown>) => Object.entries(io)
  .sort(([a], [b]) => Number(a) - Number(b))
  .map(([k, v]) => `${k}=${typeof v === 'boolean' ? (v ? 'on' : 'off') : typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join('  ');

export function RawLog({ trackerId }: { trackerId: number }) {
  const [period, setPeriod] = useState<Period>('1h');
  const [page, setPage] = useState<(RecordsPage & { from: number; to: number }) | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(0);

  useEffect(() => {
    let live = true;
    const to = Date.now();
    const from = to - PERIODS.find((p) => p[0] === period)![2] * 3600_000;
    setPage(null);
    trackers.records(trackerId, new Date(from).toISOString(), new Date(to).toISOString())
      .then((p) => { if (live) { setPage({ ...p, from, to }); setError(''); } }).catch((e) => live && setError((e as Error).message));
    return () => { live = false; };
  }, [trackerId, period, round]);

  const csv = async () => {
    if (!page) return;
    setBusy(true);
    setError('');
    try {
      await trackers.recordsCsv(trackerId, new Date(page.from).toISOString(), new Date(page.to).toISOString());
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  const names = new Map(page?.io.map((i) => [i.io, i.name]) ?? []);
  return (
    <section className="card form rawlog" aria-label="Raw data log">
      <div className="page-head">
        <h2>Raw data log</h2>
        <div className="actions wrap">
          <div className="seg" role="group" aria-label="Period">
            {PERIODS.map(([k, label]) => <button key={k} type="button" aria-pressed={period === k} onClick={() => setPeriod(k)}>{label}</button>)}
          </div>
          <button type="button" className="link" onClick={() => setRound((n) => n + 1)}>Refresh</button>
        </div>
      </div>
      <p className="muted small">
        Every record as received, newest first. “Delay” is how long after recording it reached us (buffered data shows here).
        The table shows up to 1,000 records; <b>All records as CSV</b> has every record of the period with one column per value.
      </p>
      {error && <p className="error" role="alert">{error}</p>}
      {!page && !error && <p className="muted">Loading…</p>}
      {page && (
        <>
          <div className="actions wrap">
            <button type="button" className="button ghost small" onClick={() => void csv()} disabled={busy || page.records.length === 0}>
              {busy ? 'Preparing…' : 'All records as CSV'}
            </button>
            {page.more && <span className="muted small">More than {page.limit.toLocaleString('en-IE')} records in this period: the table shows the newest {page.limit.toLocaleString('en-IE')}.</span>}
          </div>
          <DataGrid id="rawlog" rows={page.records} rowKey={(r) => r.fixTime} noun={['record', 'records']} defaultSort={{ key: 't', dir: -1 }}
            empty="No records in this period." columns={[
              { key: 't', label: 'Recorded', always: true, value: (r) => r.fixTime, sortBy: (r) => Date.parse(r.fixTime),
                cell: (r) => <span className="num">{period === '7d' ? `${dayLabel(r.fixTime)} ` : ''}{time(r.fixTime)}:{new Date(r.fixTime).getSeconds().toString().padStart(2, '0')}</span>, nowrap: true },
              { key: 'delay', label: 'Delay', align: 'right', value: (r) => (r.received ? Math.round((Date.parse(r.received) - Date.parse(r.fixTime)) / 1000) : null),
                cell: (r) => { if (!r.received) return '–'; const s = (Date.parse(r.received) - Date.parse(r.fixTime)) / 1000; return s < 60 ? `${Math.max(0, Math.round(s))} s` : duration(s / 60); } },
              { key: 'pos', label: 'Position', value: (r) => `${r.lat.toFixed(5)}, ${r.lon.toFixed(5)}`, nosort: true, cell: (r) => <span className="mono">{r.lat.toFixed(5)}, {r.lon.toFixed(5)}</span> },
              { key: 'speed', label: 'km/h', align: 'right', value: (r) => (r.speedKmh === null ? null : Math.round(r.speedKmh)) },
              { key: 'course', label: 'Course', align: 'right', hidden: true, value: (r) => r.course },
              { key: 'alt', label: 'Alt. (m)', align: 'right', hidden: true, value: (r) => r.altitudeM },
              { key: 'sats', label: 'Sats', align: 'right', value: (r) => r.satellites },
              { key: 'hdop', label: 'HDOP', align: 'right', hidden: true, value: (r) => r.hdop },
              { key: 'valid', label: 'Fix', value: (r) => (r.valid ? 'yes' : 'no'), cell: (r) => (r.valid ? 'yes' : <b className="danger">no</b>) },
              { key: 'power', label: 'Supply (V)', align: 'right', value: (r) => (r.powerV === null ? null : Math.round(r.powerV * 100) / 100) },
              { key: 'source', label: 'Source', hidden: true, value: (r) => r.source },
              { key: 'io', label: 'Values (IO = as stored)', value: (r) => ioText(r.io), nosort: true,
                cell: (r) => <span className="rawlog-io" title={Object.keys(r.io).map((k) => `IO ${k}${names.get(k) ? ` ${names.get(k)}` : ''}`).join('\n')}>{ioText(r.io)}</span> },
            ]} />
        </>
      )}
    </section>
  );
}
